import React, { useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';

const InputList = ({
  items = [],
  onItemsChange,
  label,
  placeholder = "Enter text...",
  maxItems = null,
  emptyMessage = "No items added yet"
}) => {
  const [inputValue, setInputValue] = useState('');

  // Handle adding a new item
  const handleAddItem = () => {
    const trimmedValue = inputValue.trim();
    
    if (!trimmedValue) return;
    
    // Check for duplicates
    if (items.some(item => item.title.toLowerCase() === trimmedValue.toLowerCase())) {
      return;
    }
    
    // Check max items limit
    if (maxItems && items.length >= maxItems) {
      return;
    }
    
    const newItems = [...items, { title: trimmedValue, subtitle: '' }];
    onItemsChange(newItems);
    setInputValue('');
  };

  // Handle removing an item
  const handleRemoveItem = (indexToRemove) => {
    const newItems = items.filter((_, index) => index !== indexToRemove);
    onItemsChange(newItems);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Check if add button should be disabled
  const isAddDisabled = !inputValue.trim() || 
    items.some(item => item.title.toLowerCase() === inputValue.trim().toLowerCase()) ||
    (maxItems && items.length >= maxItems);

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-base font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      {/* Input Section */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          onClick={handleAddItem}
          disabled={isAddDisabled}
          className={`px-4 py-2 rounded-md flex items-center gap-1 font-medium transition-colors ${
            isAddDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }`}
        >
          <FiPlus size={16} />
          Add
        </button>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {items.length > 0 && (
          items.map((item, index) => (
            <div
              key={index}
              className="flex items-start justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            >
                <div className="flex-1">
                  <span className="text-gray-800">{item.title}</span>
                  {item.subtitle && item.subtitle.trim() && (
                    <div className="text-xs text-gray-600 mt-1">
                      {item.subtitle}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded p-1 ml-2 flex-shrink-0"
                  title="Remove item"
                >
                  <FiX size={16} />
                </button>
            </div>
          ))
        )}
      </div>

      {/* Item count and max items indicator */}
      {(items.length > 0 || maxItems) && (
        <div className="text-xs text-gray-500 mt-2">
          {items.length} item{items.length !== 1 ? 's' : ''}
          {maxItems && ` (max ${maxItems})`}
        </div>
      )}
    </div>
  );
};

export default InputList;
